"use client";

import { cva } from "class-variance-authority";
import { MoreHorizontal } from "lucide-react";
import {
  Children,
  type ElementType,
  type FC,
  type HTMLAttributes,
  isValidElement,
  type MouseEvent,
  type ReactNode,
  useState,
} from "react";
import { CountBadge } from "@/components/ui/count-badge";
import { DrawerDialog } from "@/components/ui/drawer-dialog";
import { Link } from "@/components/ui/link";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import {
  SidebarPanelContext,
  SidebarPanelContextValue,
  useSidebarPanel,
} from "./sidebar-panel.context";

export type SidebarPanelLayout = Extract<Layout, "desktop" | "mobile">;

export interface SidebarPanelProps extends HTMLAttributes<HTMLElement> {
  layout?: SidebarPanelLayout;
  activeValue?: string;
  as?: ElementType;
}

const ROOT_VARIANTS = cva("surface-card shadow-sm", {
  variants: { layout: { desktop: "p-5", mobile: "p-4" } },
});

const Root: FC<SidebarPanelProps> = ({
  layout = "desktop",
  activeValue,
  as: Tag = "aside",
  className,
  children,
  ...props
}) => {
  const [moreOpen, setMoreOpen] = useState(false);

  const contextValue: SidebarPanelContextValue = {
    layout,
    activeValue,
    placement: "bar",
    moreOpen,
    setMoreOpen,
  };

  return (
    <SidebarPanelContext.Provider value={contextValue}>
      <Tag data-layout={layout} className={cn(ROOT_VARIANTS({ layout }), className)} {...props}>
        {children}
      </Tag>
    </SidebarPanelContext.Provider>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

const Header: FC<PartProps> = ({ className, children }) => (
  <div className={cn("flex flex-col items-center text-center", className)}>{children}</div>
);

const AVATAR_VARIANTS = cva(
  "flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-accent bg-primary font-display font-bold text-on-primary",
  {
    variants: {
      layout: { desktop: "h-20 w-20 text-h5", mobile: "h-14 w-14 text-h6" },
    },
  },
);

const Avatar: FC<PartProps> = ({ className, children }) => {
  const { layout } = useSidebarPanel();

  return <div className={cn(AVATAR_VARIANTS({ layout }), className)}>{children}</div>;
};

const Title: FC<PartProps> = ({ className, children }) => (
  <p className={cn("mt-3 font-display text-h6 font-bold text-fg-strong", className)}>{children}</p>
);

const Description: FC<PartProps> = ({ className, children }) => (
  <p className={cn("mt-1 text-body-sm text-fg-muted", className)}>{children}</p>
);

type GovBrLevelKey = "bronze" | "silver" | "gold";

const GOV_BR_LEVELS: Record<GovBrLevelKey, { label: string; className: string }> = {
  bronze: { label: "Bronze", className: "bg-selo-bronze" },
  silver: { label: "Prata", className: "bg-selo-prata" },
  gold: { label: "Ouro", className: "bg-selo-ouro" },
};

const GOV_BR_LEVEL_ORDER = Object.keys(GOV_BR_LEVELS) as GovBrLevelKey[];

interface GovBrLevelProps {
  level: GovBrLevelKey;
  className?: string;
}

const GovBrLevel: FC<GovBrLevelProps> = ({ level, className }) => {
  const reached = GOV_BR_LEVEL_ORDER.indexOf(level);
  const { label, className: levelClassName } = GOV_BR_LEVELS[level];

  return (
    <div className={cn("mt-4 w-full", className)}>
      <div className="flex gap-1">
        {GOV_BR_LEVEL_ORDER.map((step, i) => (
          <span
            key={step}
            className={cn(
              "h-1.5 flex-1 rounded-pill",
              i <= reached ? levelClassName : "bg-bg-secondary",
            )}
          />
        ))}
      </div>
      <p className="mt-2 text-body-xs text-fg-muted">
        Sua conta <strong>gov.br</strong> é nível{" "}
        <strong className="text-fg-strong">{label}</strong>
      </p>
    </div>
  );
};

const NAV_VARIANTS = cva("border-t border-border-subtle", {
  variants: { layout: { desktop: "mt-5 pt-4", mobile: "mt-4 pt-3" } },
});

const NAV_LIST_VARIANTS = cva("gap-1", {
  variants: {
    layout: {
      desktop: "flex flex-col",
      mobile: "grid auto-cols-fr grid-flow-col",
    },
  },
});

const ENTRY_VARIANTS = cva(
  "rounded-lg font-semibold no-underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link",
  {
    variants: {
      appearance: {
        desktop: "flex w-full items-center gap-3 px-3 py-2.5 text-left text-body-sm",
        mobile: "flex w-full flex-col items-center gap-1 px-2 py-2.5 text-center text-body-xs",
        more: "flex w-full items-center gap-3 px-3 py-3.5 text-left text-body-md",
      },
      active: {
        true: "bg-bg-tint text-primary",
        false: "text-fg-default hover:bg-bg-secondary",
      },
    },
    compoundVariants: [{ appearance: "more", active: false, className: "text-fg-strong" }],
    defaultVariants: { active: false },
  },
);

const ENTRY_LABEL_VARIANTS = cva("", {
  variants: {
    appearance: {
      desktop: "flex-1",
      mobile: "break-words leading-tight",
      more: "flex-1",
    },
  },
});

interface NavProps extends PartProps {
  label: string;
  mobileVisibleCount?: number;
  moreLabel?: string;
  moreIcon?: ReactNode;
}

/** In the `mobile` layout, children after `mobileVisibleCount` go into "More". */
const Nav: FC<NavProps> = ({
  label,
  mobileVisibleCount,
  moreLabel = "Mais",
  moreIcon = <MoreHorizontal />,
  className,
  children,
}) => {
  const ctx = useSidebarPanel();
  const { layout, activeValue, moreOpen, setMoreOpen } = ctx;
  const moreTitleId = useStableId();

  const entries = Children.toArray(children);
  const splitAt =
    layout === "mobile" && mobileVisibleCount !== undefined ? mobileVisibleCount : entries.length;
  const barEntries = entries.slice(0, splitAt);
  const moreEntries = entries.slice(splitAt);
  const moreActive =
    activeValue !== undefined &&
    moreEntries.some(
      (entry) => isValidElement<{ value?: string }>(entry) && entry.props.value === activeValue,
    );

  return (
    <nav
      aria-label={label}
      data-layout={layout}
      className={cn(NAV_VARIANTS({ layout }), className)}
    >
      <ul className={NAV_LIST_VARIANTS({ layout })}>
        {barEntries}
        {moreEntries.length > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className={ENTRY_VARIANTS({
                appearance: "mobile",
                active: moreActive,
              })}
            >
              <EntryIcon>{moreIcon}</EntryIcon>
              <span className={ENTRY_LABEL_VARIANTS({ appearance: "mobile" })}>{moreLabel}</span>
            </button>
          </li>
        )}
      </ul>

      {moreEntries.length > 0 && (
        <DrawerDialog
          title={moreLabel}
          labelledBy={moreTitleId}
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
        >
          <ul className="space-y-1 px-3 py-3">
            <SidebarPanelContext.Provider value={{ ...ctx, placement: "more" }}>
              {moreEntries}
            </SidebarPanelContext.Provider>
          </ul>
        </DrawerDialog>
      )}
    </nav>
  );
};

type EntryAppearance = SidebarPanelLayout | "more";

const useEntry = () => {
  const { layout, placement, setMoreOpen } = useSidebarPanel();
  const appearance: EntryAppearance = placement === "more" ? "more" : layout;
  const closeMore = () => {
    if (placement === "more") setMoreOpen(false);
  };
  return { appearance, closeMore };
};

const EntryIcon: FC<{ children: ReactNode }> = ({ children }) => (
  <span aria-hidden className="relative flex shrink-0 [&>svg]:h-5 [&>svg]:w-5">
    {children}
  </span>
);

interface EntryContentProps {
  appearance: EntryAppearance;
  icon?: ReactNode;
  badge?: number;
  badgeLabel?: string;
  children: ReactNode;
}

const EntryContent: FC<EntryContentProps> = ({ appearance, icon, badge, badgeLabel, children }) => {
  const hasBadge = badge !== undefined && badge > 0;
  return (
    <>
      {icon && (
        <EntryIcon>
          {icon}
          {appearance === "mobile" && <CountBadge count={badge ?? 0} />}
        </EntryIcon>
      )}
      <span className={ENTRY_LABEL_VARIANTS({ appearance })}>{children}</span>
      {hasBadge && appearance !== "mobile" && (
        <span
          aria-hidden
          className="rounded-pill bg-feedback-danger px-2 py-0.5 text-body-xs font-bold text-on-primary"
        >
          {badge}
        </span>
      )}
      {hasBadge && <span className="sr-only">{badgeLabel ?? `(${badge})`}</span>}
    </>
  );
};

interface ItemProps extends PartProps {
  value?: string;
  href: string;
  icon?: ReactNode;
  badge?: number;
  badgeLabel?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

const Item: FC<ItemProps> = ({
  value,
  href,
  icon,
  badge,
  badgeLabel,
  onClick,
  className,
  children,
}) => {
  const { activeValue } = useSidebarPanel();
  const { appearance, closeMore } = useEntry();
  const active = value !== undefined && value === activeValue;

  return (
    <li>
      <Link
        href={href}
        aria-current={active ? "page" : undefined}
        data-active={active || undefined}
        className={cn(ENTRY_VARIANTS({ appearance, active }), className)}
        onClick={(e) => {
          closeMore();
          onClick?.(e);
        }}
      >
        <EntryContent appearance={appearance} icon={icon} badge={badge} badgeLabel={badgeLabel}>
          {children}
        </EntryContent>
      </Link>
    </li>
  );
};

interface ActionProps extends PartProps {
  icon?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const Action: FC<ActionProps> = ({ icon, onClick, className, children }) => {
  const { appearance, closeMore } = useEntry();

  return (
    <li>
      <button
        type="button"
        className={cn(ENTRY_VARIANTS({ appearance }), className)}
        onClick={(e) => {
          closeMore();
          onClick?.(e);
        }}
      >
        <EntryContent appearance={appearance} icon={icon}>
          {children}
        </EntryContent>
      </button>
    </li>
  );
};

Root.displayName = "SidebarPanel";
Header.displayName = "SidebarPanel.Header";
Avatar.displayName = "SidebarPanel.Avatar";
Title.displayName = "SidebarPanel.Title";
Description.displayName = "SidebarPanel.Description";
GovBrLevel.displayName = "SidebarPanel.GovBrLevel";
Nav.displayName = "SidebarPanel.Nav";
Item.displayName = "SidebarPanel.Item";
Action.displayName = "SidebarPanel.Action";

export const SidebarPanel = Object.assign(Root, {
  Header,
  Avatar,
  Title,
  Description,
  GovBrLevel,
  Nav,
  Item,
  Action,
});
